import type { DocketList, DocketRepeat, DocketTask } from './docketModel'
import { addDaysToKey, dayOfWeek, daysBetween } from './writingDays'

// Dates for Docket: where a repeating task goes next, which tasks belong to
// Today and Upcoming, and the short words the add bar understands.

// ── Repeats ─────────────────────────────────────────────────────────────────

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

const pad = (n: number) => String(n).padStart(2, '0')

/** The Nth of a month, or the month's last day when it is shorter. */
function monthDay(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(Math.min(day, daysInMonth(year, month)))}`
}

/**
 * The next date a repeat lands on after `from`, never on or before
 * `notBefore` (the day it was finished), so a task finished late moves to its
 * next date to come rather than one already past.
 */
export function nextRepeatDate(repeat: DocketRepeat, from: string, finishedOn: string): string {
  const floor = from > finishedOn ? from : finishedOn
  switch (repeat.kind) {
    case 'after':
      return addDaysToKey(finishedOn, repeat.days)
    // An event's prep task moves with the event (see utils/prepTasks); this
    // is only a fallback for when the event can't be found.
    case 'event':
      return addDaysToKey(floor, 7)
    case 'daily': {
      let next = addDaysToKey(from, repeat.every)
      while (next <= finishedOn) next = addDaysToKey(next, repeat.every)
      return next
    }
    case 'weekly': {
      let next = addDaysToKey(floor, 1)
      while (!repeat.days.includes(dayOfWeek(next))) next = addDaysToKey(next, 1)
      return next
    }
    case 'monthly': {
      let [year, month] = floor.split('-').map(Number)
      let next = monthDay(year, month, repeat.day)
      while (next <= floor) {
        month += 1
        if (month > 12) { month = 1; year += 1 }
        next = monthDay(year, month, repeat.day)
      }
      return next
    }
  }
}

/**
 * A repeating task's dates once it is finished on `finishedOn`. The day to do
 * it moves to the next repeat; a deadline keeps its distance from that day.
 * A task with only a deadline repeats by the deadline.
 */
export function nextTaskDates(task: Pick<DocketTask, 'doOn' | 'dueOn' | 'repeat'>, finishedOn: string): { doOn: string | null; dueOn: string | null } {
  if (!task.repeat) return { doOn: task.doOn, dueOn: task.dueOn }
  const anchor = task.doOn ?? task.dueOn ?? finishedOn
  const next = nextRepeatDate(task.repeat, anchor, finishedOn)
  if (task.doOn) {
    return {
      doOn: next,
      dueOn: task.dueOn ? addDaysToKey(next, daysBetween(task.doOn, task.dueOn)) : null,
    }
  }
  if (task.dueOn) return { doOn: null, dueOn: next }
  return { doOn: next, dueOn: null }
}

// ── Today and Upcoming ─────────────────────────────────────────────────────

/** The day a task shows under in Upcoming and on the calendar. */
export function taskDay(task: Pick<DocketTask, 'doOn' | 'dueOn'>): string | null {
  return task.doOn ?? task.dueOn
}

/** A deadline that has passed on an unfinished task. */
export function isOverdue(task: Pick<DocketTask, 'done' | 'dueOn'>, today: string): boolean {
  return !task.done && !!task.dueOn && task.dueOn < today
}

/** What Today holds, as the writer chose (Settings > Docket). */
export interface TodayRule {
  /** Unfinished tasks from earlier days stay in Today. */
  carryOver: boolean
  /** Deadlines this many days off show in Today too. */
  dueWithin: number
}

const DEFAULT_TODAY_RULE: TodayRule = { carryOver: true, dueWithin: 0 }

/**
 * Whether an unfinished task belongs in Today: planned for today (or an
 * earlier day, carried over), or due by today (or within the days asked).
 */
export function isForToday(task: Pick<DocketTask, 'done' | 'doOn' | 'dueOn'>, today: string, rule: TodayRule = DEFAULT_TODAY_RULE): boolean {
  if (task.done) return false
  const dueBy = rule.dueWithin > 0 ? addDaysToKey(today, rule.dueWithin) : today
  const planned = !!task.doOn && (rule.carryOver ? task.doOn <= today : task.doOn === today)
  const due = !!task.dueOn && (rule.carryOver ? task.dueOn <= dueBy : task.dueOn >= today && task.dueOn <= dueBy)
  return planned || due
}

export interface UpcomingGroup {
  /** 'YYYY-MM-DD' for a day, 'YYYY-MM' for a later month. */
  key: string
  kind: 'day' | 'month'
  tasks: DocketTask[]
}

/** Dated tasks after today: a group a day for two weeks (or as many days as asked), then a group a month. */
export function upcomingGroups(tasks: DocketTask[], today: string, days = 14, rule: TodayRule = DEFAULT_TODAY_RULE): UpcomingGroup[] {
  const horizon = addDaysToKey(today, days)
  const groups = new Map<string, UpcomingGroup>()
  const dated = tasks
    .filter((task) => !task.done && !isForToday(task, today, rule) && (taskDay(task) ?? '') > today)
    .sort((a, b) => taskDay(a)!.localeCompare(taskDay(b)!) || a.order - b.order)
  for (const task of dated) {
    const when = taskDay(task)!
    const near = when <= horizon
    const key = near ? when : when.slice(0, 7)
    let group = groups.get(key)
    if (!group) groups.set(key, (group = { key, kind: near ? 'day' : 'month', tasks: [] }))
    group.tasks.push(task)
  }
  return [...groups.values()]
}

// ── The add bar ────────────────────────────────────────────────────────────

export interface QuickToken {
  kind: 'do' | 'due' | 'time' | 'list'
  /** The words as typed, e.g. "oct 12", "3pm" or "#Errands". */
  raw: string
  /** 'YYYY-MM-DD' for dates, 'HH:MM' for a time, the list id for a list. */
  value: string
}

export interface QuickAdd {
  title: string
  doOn: string | null
  doAt: string | null
  dueOn: string | null
  dueAt: string | null
  listId: string | null
  tokens: QuickToken[]
}

/** What the add bar makes of text it doesn't read: just a title. */
export function plainQuickAdd(input: string): QuickAdd {
  return { title: input.trim(), doOn: null, doAt: null, dueOn: null, dueAt: null, listId: null, tokens: [] }
}

/** The time some words name, as 'HH:MM': "3pm", "3:30pm", "9am", "15:30". */
export function parseTimeWords(words: string): string | null {
  const w = words.toLowerCase().trim()
  const twelve = w.match(/^(\d{1,2})(?::([0-5]\d))?\s?(am|pm|a|p)$/)
  if (twelve) {
    const hour = Number(twelve[1])
    if (hour < 1 || hour > 12) return null
    const h24 = (hour % 12) + (twelve[3].startsWith('p') ? 12 : 0)
    return `${pad(h24)}:${twelve[2] ?? '00'}`
  }
  const clock = w.match(/^([01]?\d|2[0-3]):([0-5]\d)$/)
  return clock ? `${pad(Number(clock[1]))}:${clock[2]}` : null
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

function weekdayIndex(word: string): number {
  if (word.length < 3) return -1
  return WEEKDAYS.findIndex((name) => name.startsWith(word))
}

function monthIndex(word: string): number {
  if (word.length < 3) return -1
  return MONTHS.findIndex((name) => name.startsWith(word))
}

/** The date some words name, or null: today, tomorrow, a weekday, or a month and day. */
export function parseDateWords(words: string, today: string): string | null {
  const w = words.toLowerCase().trim()
  if (w === 'today' || w === 'tod') return today
  if (w === 'tomorrow' || w === 'tom' || w === 'tmrw') return addDaysToKey(today, 1)
  const weekday = weekdayIndex(w)
  if (weekday >= 0 && !w.includes(' ')) {
    // The next one to come; "today" names today.
    let next = addDaysToKey(today, 1)
    while (dayOfWeek(next) !== weekday) next = addDaysToKey(next, 1)
    return next
  }
  const match = w.match(/^([a-z]+)\.? (\d{1,2})$/) ?? w.match(/^(\d{1,2}) ([a-z]+)\.?$/)
  if (match) {
    const [monthWord, dayWord] = /^\d/.test(match[1]) ? [match[2], match[1]] : [match[1], match[2]]
    const month = monthIndex(monthWord)
    const dayNumber = Number(dayWord)
    if (month < 0 || dayNumber < 1) return null
    let year = Number(today.slice(0, 4))
    if (dayNumber > daysInMonth(year, month + 1)) return null
    let key = `${year}-${pad(month + 1)}-${pad(dayNumber)}`
    if (key < today) {
      year += 1
      if (dayNumber > daysInMonth(year, month + 1)) return null
      key = `${year}-${pad(month + 1)}-${pad(dayNumber)}`
    }
    return key
  }
  return null
}

/** Folds a list name for matching "#GroceryList" against "Grocery list". */
const fold = (name: string) => name.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')

function findList(word: string, lists: DocketList[]): DocketList | null {
  const wanted = fold(word)
  if (!wanted) return null
  return lists.find((list) => fold(list.name) === wanted)
    ?? lists.find((list) => fold(list.name).startsWith(wanted))
    ?? null
}

/**
 * Reads the add bar. Words at the end can set the day to do it ("tomorrow",
 * "fri", "oct 12"), a deadline ("!fri"), a time ("3pm", "at 15:30"), or the
 * list ("#Errands"); the rest is the title. Words the writer chose to keep as
 * text (`kept`) stop the reading. When the writer's dates are all deadlines
 * (`dates` 'due'), a plain date is a deadline; when they are all days to do
 * things ('do'), "!fri" is one of those.
 */
export function parseQuickAdd(input: string, today: string, lists: DocketList[], kept: string[] = [], dates: 'both' | 'do' | 'due' = 'both'): QuickAdd {
  const result = plainQuickAdd(input)
  let time: string | null = null
  let rest = input.trim()

  const take = (): boolean => {
    const words = rest.split(/\s+/)
    if (words.length < 2) return false // Never leave the title empty.
    const last = words[words.length - 1]
    const lastTwo = words.length >= 3 ? words.slice(-2).join(' ') : null

    const accept = (raw: string, token: QuickToken | null) => {
      if (!token || kept.includes(raw)) return false
      result.tokens.unshift(token)
      rest = rest.slice(0, rest.length - raw.length).trimEnd()
      return true
    }

    if (last.startsWith('#') && result.listId === null) {
      const list = findList(last.slice(1), lists)
      if (list && accept(last, { kind: 'list', raw: last, value: list.id })) {
        result.listId = list.id
        return true
      }
      return false
    }

    if (time === null && result.doOn === null && result.dueOn === null) {
      // "at 3pm" or "3pm", or "3 pm"; only before any date, as in "fri 3pm".
      const lastThree = words.length >= 4 ? words.slice(-3).join(' ') : null
      for (const raw of [lastThree, lastTwo, last]) {
        if (!raw) continue
        const value = parseTimeWords(raw.replace(/^at\s+/i, ''))
        if (!value || (raw === lastThree && !/^at\s/i.test(raw))) continue
        if (accept(raw, { kind: 'time', raw, value })) {
          time = value
          return true
        }
        return false
      }
    }

    for (const raw of [lastTwo, last]) {
      if (!raw) continue
      const marked = raw.startsWith('!')
      const due = dates === 'due' || (marked && dates === 'both')
      if (due ? result.dueOn !== null : result.doOn !== null) continue
      const date = parseDateWords(marked ? raw.slice(1) : raw, today)
      if (!date) continue
      if (accept(raw, { kind: due ? 'due' : 'do', raw, value: date })) {
        if (due) result.dueOn = date
        else result.doOn = date
        return true
      }
      return false
    }
    return false
  }

  while (take()) { /* keep reading words from the end */ }
  result.title = rest.trim()
  if (time) {
    // A time goes with the day to do it, else the deadline; on its own, it is today.
    if (!result.doOn && !result.dueOn) {
      if (dates === 'due') result.dueOn = today
      else result.doOn = today
    }
    if (result.doOn) result.doAt = time
    else result.dueAt = time
  }
  return result
}
